import {memo} from 'react';

import {isNumber} from 'lodash';
import styled from 'styled-components';

import {PriceLabel} from '~/shared/components/PriceLabels';
import {getLocalizationService} from '~/shared/services/localisationService';
import {UserReportOrder} from '~/shared/store/models';
import {mapTranslate} from '~/shared/consts/userTransactionsConsts';

import {Col, InnerRow, Line, LineTitle} from './shared.style';
import TwoLinesPayment from './TwoLinesPayment';

const BoldPrice = styled(PriceLabel)`
  font-weight: bold;
`;

const OrderWrapper = styled.div`
  padding: 15px;
  color: ${({theme}) => theme.userReport.showOrder.text};
`;

const Ltr = styled.div`
  direction: ltr;
`;

type OrderProps = {
  order: UserReportOrder;
  shouldShowCommentsRows?: boolean;
};

const Order = ({order, shouldShowCommentsRows}: OrderProps) => {
  const {t, currentLanguageKey} = getLocalizationService();

  const {
    restaurantName,
    orderTotal,
    dateAndTime,
    orderType,
    products,
    payments,
    deliveryComments,
    employeePart,
    employerPart,
    showEmployerEmployeePart,
    prepaidBalance,
    shouldShowPaymentRemarks,
    isAutoRefundRemarksPatchIncluded,
    showGrossNet,
    transactionBrutoSum,
    transactionNetSum,
    guestType,
    isKlaReport,
    totalSummary,
    orderPrice,
    orderStatus,
  } = order;
  const shouldShowPrePaid = isNumber(prepaidBalance);
  const shouldShowProducts = !!(products && products.length);
  const shouldShowPayments = !!(payments && payments.length && !showGrossNet);

  // TODO
  // support ts in shared (mapTranslate)
  const mappedTranslate = mapTranslate as any;

  return (
    <OrderWrapper>
      <InnerRow bold>
        <div>{restaurantName}</div>
        <PriceLabel price={orderTotal || orderPrice || 0} />
      </InnerRow>
      {shouldShowPrePaid && (
        <InnerRow>
          <div>{t('prepaid_balance')}</div>
          <PriceLabel price={prepaidBalance} />
        </InnerRow>
      )}
      <InnerRow>
        <div>{dateAndTime}</div>
        <div>{orderStatus || t(mappedTranslate[orderType] || orderType)}</div>
      </InnerRow>

      {showEmployerEmployeePart && (
        <TwoLinesPayment
          title={t('division')}
          data={[
            {
              rowTitle: t('employer_part'),
              price: employerPart,
            },
            {
              rowTitle: t('employee_part'),
              price: employeePart,
            },
          ]}
        />
      )}

      {shouldShowProducts && (
        <Line paddingTop borderTop>
          <LineTitle>{t('products')}</LineTitle>
          <Col>
            {products.map(({name, price}) => (
              <InnerRow key={name}>
                <Line>
                  <div>{name}</div>
                </Line>
                <PriceLabel price={price} />
              </InnerRow>
            ))}
          </Col>
        </Line>
      )}

      {deliveryComments && shouldShowCommentsRows && (
        <Line paddingTop borderTop>
          <LineTitle>{t('order_notes')}</LineTitle>
          <Col>{deliveryComments}</Col>
        </Line>
      )}
      {shouldShowPayments && (
        <Line paddingTop borderTop>
          <LineTitle>{t('payment')}</LineTitle>
          <Col>
            {payments.map(({transactionId, paymentMethod, cardSuffix, paymentRemarks, autoRefundRemarks, total}) => {
              const shouldShowautoRefundRemarks = isAutoRefundRemarksPatchIncluded && autoRefundRemarks;
              if (shouldShowautoRefundRemarks) {
                return (
                  <div key={`${transactionId}_${total}`}>
                    <InnerRow>
                      <Line>
                        <Ltr>{autoRefundRemarks}</Ltr>
                      </Line>
                      <PriceLabel price={total} />
                    </InnerRow>
                  </div>
                );
              }

              return (
                <div key={`${transactionId}_${total}`}>
                  <InnerRow>
                    <Line>
                      <Ltr>
                        {currentLanguageKey === 'he'
                          ? `${cardSuffix || ''} ${t(paymentMethod.toLowerCase())}`
                          : `${t(paymentMethod.toLowerCase())} ${cardSuffix || ''}`}
                      </Ltr>
                    </Line>
                    <PriceLabel price={total} />
                  </InnerRow>
                  {shouldShowPaymentRemarks && (
                    <InnerRow key={`_${transactionId}`} flexStart>
                      <LineTitle>{t('payment_remark_case')}</LineTitle>
                      {paymentRemarks}
                    </InnerRow>
                  )}
                </div>
              );
            })}
          </Col>
        </Line>
      )}
      {showGrossNet && (
        <TwoLinesPayment
          title={t('division')}
          data={[
            {
              rowTitle: t('gross'),
              price: transactionBrutoSum,
            },
            {
              rowTitle: t('nett'),
              price: transactionNetSum,
            },
          ]}
        />
      )}
      {guestType && guestType.toLowerCase() && (
        <Line paddingTop borderTop>
          <LineTitle>{t('guests')}</LineTitle>
          <Col>{t(guestType.toLowerCase()) || guestType}</Col>
        </Line>
      )}
      {isKlaReport && (
        <>
          <TwoLinesPayment
            title={t('division')}
            data={[
              {
                rowTitle: t('subsidized_meals'),
                text: employerPart,
              },
              {
                rowTitle: t('nett_meals'),
                text: employeePart,
              },
            ]}
          />
          <Line paddingTop borderTop>
            <LineTitle>{t('daily_summary')}</LineTitle>
            <Col singleRow>
              <BoldPrice price={totalSummary || orderPrice || 0} />
            </Col>
          </Line>
        </>
      )}
    </OrderWrapper>
  );
};

export default memo(Order);
